<script setup lang="ts">
  import { ref, computed, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { Plus, Lock, X, Clock } from "lucide-vue-next";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useResultsV2PopulationsStore } from "@stores/survey/resultsV2/resultsV2.populations";
  import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
  import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
  import { Button } from "@/components/ui/button";
  import PopulationEditor from "./PopulationEditor.vue";
  import { useFilterableCards } from "./useFilterableCards";

  type Filters = Record<string, Record<string, boolean>>;

  // number = index of a population attached to the view.
  type Selection = number | "all";

  interface SavedPopulation {
    id: number;
    name: string;
    filters: Filters;
    token: string;
    color: string | null;
  }

  interface ViewPopulation {
    id: number | string;
    ephemeral?: boolean;
    hidden?: boolean;
    _fromAll?: boolean;
    search_group?: SavedPopulation;
  }

  const props = defineProps<{
    open: boolean;
    initialSelection?: Selection;
  }>();

  const emit = defineEmits<{
    close: [];
  }>();

  const store = useResultsV2Store();
  const populationsStore = useResultsV2PopulationsStore();
  const { populations } = storeToRefs(populationsStore);
  const { savedPopulations, effectiveShowAllPopulation } = storeToRefs(store);
  const { filterableCards, fetchFilterableCards } = useFilterableCards();

  const selection = ref<Selection>("all");
  const saving = ref(false);

  const pendingDelete = ref<{ token: string; name: string } | null>(null);

  const isAll = computed(() => selection.value === "all");

  const currentPopulation = computed<ViewPopulation | null>(() =>
    typeof selection.value === "number" ? populations.value[selection.value] || null : null,
  );

  const isEphemeral = computed(() => !!currentPopulation.value?.ephemeral);

  const isFromAll = computed(() => !!currentPopulation.value?._fromAll);

  const isDirty = computed(
    () => !!currentPopulation.value && populationsStore.isDirty(currentPopulation.value),
  );

  const attachedSearchGroupIds = computed(
    () => new Set(populations.value.map((p: ViewPopulation) => p.search_group?.id)),
  );

  const detachedSavedPopulations = computed(() =>
    savedPopulations.value.filter((sg: SavedPopulation) => !attachedSearchGroupIds.value.has(sg.id)),
  );

  function filterCount(pop: ViewPopulation): number {
    return Object.keys(pop.search_group?.filters || {}).length;
  }

  function resolveColor(pop: ViewPopulation, index: number): string {
    if (pop.search_group?.color) return pop.search_group.color;
    return POPULATION_AUTO_COLORS[(index + 1) % POPULATION_AUTO_COLORS.length];
  }

  function select(sel: Selection) {
    selection.value = sel;
  }

  watch(
    () => props.open,
    (isOpen) => {
      if (!isOpen) return;
      fetchFilterableCards();
      select(props.initialSelection ?? "all");
    },
  );

  watch(
    () => populations.value.length,
    () => {
      if (!props.open) return;
      if (typeof selection.value === "number" && !populations.value[selection.value]) {
        select(populations.value.length ? populations.value.length - 1 : "all");
      }
    },
  );

  function refreshCharts() {
    store.fetchAllChartsData(populations.value);
  }

  const nameModel = computed<string>({
    get: () => currentPopulation.value?.search_group?.name || "",
    set: (value) => {
      if (!currentPopulation.value) return;
      populationsStore.updatePopulation(currentPopulation.value.id, { name: value });
    },
  });

  const currentColor = computed(() => currentPopulation.value?.search_group?.color || null);

  function setColor(color: string | null) {
    if (!currentPopulation.value) return;
    populationsStore.updatePopulation(currentPopulation.value.id, { color });
  }

  const currentFilters = computed<Filters>(
    () => currentPopulation.value?.search_group?.filters || {},
  );

  function handleFilterToggle(questionId: string, code: string) {
    const pop = currentPopulation.value;
    if (!pop) return;
    populationsStore.toggleFilterOnPopulation({ target: pop.id, questionId, code });
    refreshCharts();
  }

  // --- Rail row actions ---

  async function handleDetach(index: number) {
    const pop = populations.value[index];
    if (!pop) return;
    const sel = selection.value;
    await populationsStore.removePopulation(pop.id);
    refreshCharts();
    if (typeof sel === "number" && sel > index) selection.value = sel - 1;
  }

  async function handleAttach(saved: SavedPopulation) {
    await populationsStore.addPopulation({ search_group_id: saved.id });
    refreshCharts();
    const index = populations.value.findIndex((p: ViewPopulation) => p.search_group?.id === saved.id);
    if (index >= 0) select(index);
  }

  function handleNew() {
    const pop = populationsStore.createEphemeral();
    refreshCharts();
    const index = populations.value.findIndex((p: ViewPopulation) => p.id === pop.id);
    select(index >= 0 ? index : "all");
  }

  // --- Permanent deletion (saved populations only) ---

  function askDeleteSaved(sg: SavedPopulation | null | undefined) {
    if (!sg) return;
    pendingDelete.value = { token: sg.token, name: sg.name };
  }

  async function confirmDeleteSaved() {
    if (!pendingDelete.value) return;
    const { token } = pendingDelete.value;
    const attached = populations.value.find((p: ViewPopulation) => p.search_group?.token === token);
    await store.deleteSavedPopulation(token);
    // The API cascades the results_view_populations deletion; only clean up locally.
    if (attached?.search_group) populationsStore.removeBySearchGroupId(attached.search_group.id);
    refreshCharts();
    pendingDelete.value = null;
  }

  // Persist: a temporary population is promoted to a saved one, a saved population is
  // updated in place. Reselect by search_group id — promotion can move the row.
  async function handleSave() {
    const pop = currentPopulation.value;
    if (!pop || saving.value) return;
    saving.value = true;
    try {
      const saved = await populationsStore.savePopulation(pop.id);
      refreshCharts();
      if (saved) {
        const index = populations.value.findIndex((p: ViewPopulation) => p.search_group?.id === saved.id);
        if (index >= 0) select(index);
      }
    } finally {
      saving.value = false;
    }
  }
</script>

<template>
  <Sheet
    :open="open"
    @update:open="(v: boolean) => !v && emit('close')">
    <SheetContent
      side="right"
      class="flex w-[640px] flex-col sm:max-w-[640px]">
      <SheetHeader class="flex-row items-center gap-3 px-4 pt-4 pb-2">
        <SheetTitle>Populations</SheetTitle>
      </SheetHeader>

      <div class="flex min-h-0 flex-1">
        <!-- Left rail: one tab per population -->
        <div class="flex w-[200px] shrink-0 flex-col gap-0.5 overflow-y-auto border-r px-2 py-2">
          <!-- Populations on this dashboard — "All" is listed like any other -->
          <template v-if="effectiveShowAllPopulation || populations.length">
            <div class="px-2 pb-0.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
              Selected populations
            </div>
            <!-- "All": the reference population — always present, never filtered -->
            <div
              v-if="effectiveShowAllPopulation"
              class="group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13"
              :class="isAll ? 'bg-muted font-medium' : 'hover:bg-muted/60'"
              @click="select('all')">
              <div
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: POPULATION_AUTO_COLORS[0] }" />
              <span class="min-w-0 flex-1 truncate">All</span>
            </div>
            <div
              v-for="(pop, index) in populations"
              :key="pop.id || index"
              class="group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13"
              :class="[
                selection === index ? 'bg-muted font-medium' : 'hover:bg-muted/60',
                populationsStore.isDirty(pop) ? 'border border-dashed' : '',
              ]"
              @click="select(index)">
              <div
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: resolveColor(pop, index) }" />
              <span class="min-w-0 flex-1 truncate">
                {{ pop.search_group?.name }}<span v-if="populationsStore.isNarrowed(pop)">*</span>
              </span>
              <span
                v-if="populationsStore.isNarrowed(pop) && filterCount(pop)"
                class="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-3xs font-semibold tabular-nums text-background">
                {{ filterCount(pop) }}
              </span>
              <button
                :aria-label="pop.ephemeral ? 'Discard quick filter' : 'Remove from dashboard'"
                class="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                @click.stop="handleDetach(index)">
                <X class="h-3.5 w-3.5" />
              </button>
            </div>
          </template>

          <!-- Saved populations not on this dashboard: click to add + edit -->
          <template v-if="detachedSavedPopulations.length">
            <div class="mt-2 px-2 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
              Other populations
            </div>
            <div
              v-for="sg in detachedSavedPopulations"
              :key="sg.token"
              class="group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-13 opacity-60 hover:bg-muted/60 hover:opacity-100"
              @click="handleAttach(sg)">
              <div
                class="h-2.5 w-2.5 shrink-0 rounded-full"
                :style="{ backgroundColor: sg.color || 'transparent', boxShadow: sg.color ? 'none' : 'inset 0 0 0 1px hsl(var(--border))' }" />
              <span class="min-w-0 flex-1 truncate">{{ sg.name }}</span>
              <Plus class="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </template>

          <Button
            variant="outline"
            size="sm"
            class="mt-1 justify-start border-dashed font-normal text-muted-foreground"
            @click="handleNew">
            <Plus class="shrink-0" />
            New
          </Button>
        </div>

        <!-- Right editor (min-w-0: never let intrinsic content widen it past the sheet).
             Edits apply live to the dashboard; "Save" persists them. -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col">
          <!-- "All" is the reference population: no filters, always available -->
          <div
            v-if="isAll || !currentPopulation"
            class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
            <div class="flex items-start gap-2 rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
              <Lock class="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                "All" includes every respondent and can't be modified.
              </span>
            </div>
          </div>

          <PopulationEditor
            v-else
            :key="currentPopulation.id"
            v-model:name="nameModel"
            :color="currentColor"
            :filters="currentFilters"
            :cards="filterableCards"
            :can-save="!saving && (isEphemeral || isDirty)"
            :can-delete="!isEphemeral"
            :save-label="isFromAll ? 'Save as new' : 'Save'"
            @update:color="setColor"
            @toggle-filter="handleFilterToggle"
            @save="handleSave"
            @delete="askDeleteSaved(currentPopulation?.search_group)">
            <!-- Temporary population status -->
            <div
              v-if="isEphemeral"
              class="flex items-start gap-2 rounded-md border border-dashed border-status-warning/50 bg-status-warning/5 p-3 text-xs">
              <Clock class="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-warning-strong" />
              <span>
                <span class="font-medium text-status-warning-strong">Not saved yet.</span>
                Save this population to reuse it on other dashboards.
              </span>
            </div>
          </PopulationEditor>
        </div>
      </div>

      <!-- Inline confirmation: a modal would open behind the drawer, so we confirm in-place. -->
      <div
        v-if="pendingDelete !== null"
        class="flex items-center gap-2 border-t bg-destructive/5 px-4 py-3">
        <span class="min-w-0 flex-1 text-13">
          Delete <span class="font-medium">"{{ pendingDelete.name }}"</span>? This can't be undone.
        </span>
        <Button
          variant="ghost"
          size="sm"
          @click="pendingDelete = null">
          Cancel
        </Button>
        <Button
          size="sm"
          class="bg-destructive text-white hover:bg-destructive/90"
          @click="confirmDeleteSaved">
          Delete
        </Button>
      </div>
    </SheetContent>
  </Sheet>
</template>
